import { get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

const workplaceUserAccountSchema = z.object({
  _id: z.string(),
  userId: z.string(),
  name: z.string(),
});

const getWorkplaceUserAccountsResponseSchema = z.object({
  facilities: z.array(workplaceUserAccountSchema),
});

type WorkplaceUserAccount = z.infer<typeof workplaceUserAccountSchema>;

interface UseGetWorkplaceUserAccountsParams {
  name?: string;
}

interface UseGetWorkplaceUserAccountsOptions {
  enabled?: boolean;
}

export function useGetWorkplaceUserAccounts(
  params: UseGetWorkplaceUserAccountsParams = {},
  options: UseGetWorkplaceUserAccountsOptions = {}
) {
  const { name } = params;
  const { enabled = true } = options;
  const url = `${environmentConfig.REACT_APP_BASE_API_URL}/facilityUser/accounts`;

  return useQuery({
    queryKey: [url, name],
    queryFn: async () => {
      const response = await get({
        url,
        queryParams: { name },
        responseSchema: getWorkplaceUserAccountsResponseSchema,
      });
      return response.data;
    },
    enabled,
    staleTime: 60 * 1000,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_WORKPLACE_USER_ACCOUNTS_FAILURE,
      userErrorMessage: "Failed to load workplaces. Please try again.",
    },
  });
}

export type { WorkplaceUserAccount };
